import dayjs from 'dayjs';
import { useCallback, useRef } from 'react';
import { useDebouncedData } from '@hooks/useDebouncedData';
import http from '@core/fetch';
import apiConfig from '@apiConfig';
import { components } from '@apiSchema';
import {
    buildOpeningsClosingsParams,
    OpeningsClosingsQuery,
} from '@apiHooks/openingsClosingsParams.helpers';
import { MonthlyDatum } from '@pages/Home/sections/RestaurantOpeningsClosings/RestaurantOpeningsClosings.types';

type RestaurantOpeningsClosingsResponse =
    components['schemas']['RestaurantOpeningsClosingsResponse'];
type OpeningsClosingsData = components['schemas']['OpeningsClosingsData'];

const REQUEST_TYPE: keyof typeof apiConfig = 'get restaurant openings closings';
const DEBOUNCE_MS = 400;
const DEFAULT_DATA: MonthlyDatum[] = [];

export type { OpeningsClosingsQuery };

const toMonthlyDatum = (point: OpeningsClosingsData): MonthlyDatum => {
    const year = point.year ?? 0;
    const month = point.month ?? 1;
    const date = `${year}-${String(month).padStart(2, '0')}`;
    return {
        date,
        month: dayjs(`${date}-01`).format("MMM 'YY"),
        openings: point.openings ?? 0,
        closures: point.closings ?? 0,
        totalActive: point.total ?? 0,
    };
};

// fetch.js's outer .catch swallows AbortError without calling promise.reject, so
// http.get() already returns a never-settling Promise on abort — isLoading stays true
// until the follow-up debounced request resolves. No explicit AbortError handling needed.
export const useRestaurantOpeningsClosings = (query: OpeningsClosingsQuery) => {
    const controllerRef = useRef<AbortController | null>(null);

    const fetchFn = useCallback(async (q: OpeningsClosingsQuery): Promise<MonthlyDatum[]> => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;

        const response: RestaurantOpeningsClosingsResponse = await http.get(
            REQUEST_TYPE,
            buildOpeningsClosingsParams(q),
            { signal: controller.signal },
        );
        return (response?.dataPoints ?? []).map(toMonthlyDatum);
    }, []);

    return useDebouncedData(DEBOUNCE_MS, DEFAULT_DATA, fetchFn, query);
};
